<script lang="ts" setup>

interface Props {
  costCodes: ProjectCostCodeRootResponseDTO[];
  filter: 'fitterHours' | 'materials' | 'none';
}

const props = defineProps<Props>();

const selectedCostCode = defineModel<ProjectCostCodeChildResponseDTO | null>({ required: true });

const { t } = useEkI18n();

const costCodeModalFocus = ref<string>('costCodeModalFocus');
const modalIsOpen = ref(false);
const searchQuery = ref('');
const searchbar = useTemplateRef('searchbar');
const platformStore = usePlatformStore();
const { focusOnEnter } = useWebInputManager();

const allValues = computed<ProjectCostCodeRootResponseDTO[]>(() => {
  if (props.filter === 'materials') {
    return props.costCodes.map(sumCostCode => {
      return {
        ...sumCostCode,
        CostCodes: sumCostCode.CostCodes.filter(costCode => costCode.CanAddMaterials)
      };
    });
  } else if (props.filter === 'fitterHours') {
    return props.costCodes.map(sumCostCode => {
      return {
        ...sumCostCode,
        CostCodes: sumCostCode.CostCodes.filter(costCode => costCode.CanAddFitterHours)
      };
    });
  }
  
  return props.costCodes;
});

// Search on alias and name through the costCodes, not the sumCostCodes
const searchResults = computed(() => {
  if (searchQuery.value === '') {
    return allValues.value;
  }

  return allValues.value.map(sumCostCode => {
    const matchesSumCostCode = sumCostCode.Alias?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      sumCostCode.Name?.toLowerCase().includes(searchQuery.value.toLowerCase());

    const filteredCostCodes = matchesSumCostCode ? sumCostCode.CostCodes : sumCostCode.CostCodes.filter(costCode => {
      return costCode.Alias?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        costCode.Name?.toLowerCase().includes(searchQuery.value.toLowerCase());
    });

    return {
      ...sumCostCode,
      CostCodes: filteredCostCodes,
      matchesSumCostCode
    };
  }).filter(sumCostCode => {
    return sumCostCode.CostCodes.length > 0 || sumCostCode.matchesSumCostCode;
  });
});

function open() {
  modalIsOpen.value = true;
}

function close() {
  modalIsOpen.value = false;
  searchQuery.value = '';
}

function select(item: ProjectCostCodeChildResponseDTO) {
  selectedCostCode.value = item;
  close();
}

function onEnter() {
  if (!platformStore.isWeb) {
    return; 
  }

  focusOnEnter('.' + costCodeModalFocus.value);
}
 
defineExpose({
  open,
  close
});

</script>

<template>
  <Modal
    v-model:open="modalIsOpen"
    :title="t('input.selectCostCode')"
    animation-direction="horizontal"
    :close-override="close"
    @handle-dismiss="close"
    @did-present="searchbar?.$el.setFocus();"
  >
    <IonSearchbar
      ref="searchbar"
      v-model="searchQuery"
      inputmode="search"
      :placeholder="t('search.placeholder')"
      autocomplete="on"
      :debounce="500"
      @keyup.enter="onEnter"
    />

    <span
      v-if="searchResults.length === 0 && allValues.length > 0"
      class="statusMessage"
    >
      {{ t('project.noCostCodesFound') }}
    </span>
      
    <IonContent>
      <div
        v-for="sumCostCode in searchResults"
        :key="sumCostCode.ID"
      >
        <IonList v-if="sumCostCode.CostCodes.length">
          <IonListHeader>
            <IonLabel class="text-overline text-bold">
              <template v-if="sumCostCode.Alias && sumCostCode.Name">
                {{ sumCostCode.Alias }}: {{ sumCostCode.Name }}
              </template>
              <template v-else>
                {{ t('list.other') }}
              </template>
            </IonLabel>
          </IonListHeader>
          <IonItem
            v-for="costCode in sumCostCode.CostCodes"
            :key="costCode.ID" 
            :class="costCodeModalFocus"
            :cost-code="costCode"
            lines="full"
            button
            @click="select(costCode)"
          > 
            <FontAwesomeIcon
              slot="start"
              :icon="['fas', 'circle']"
              class="fa-lg"
              :style="{ color: costCode.Color }"
            />

            <IonLabel>
              {{ costCode.Alias }}: {{ costCode.Name }}
            </IonLabel>
          </IonItem>
        </IonList>
      </div>
    </IonContent>
  </Modal>
</template>

<style scoped>
ion-searchbar {
  padding-top: 1rem;
}

.statusMessage {
  padding: 0.2rem;
  font-size: small;
  color: var(--ion-color-medium);
  display: flex;
  justify-content: center;
}
 
/* remove some padding right to the arter-icon at slot start */
ion-item ion-icon {
  margin-right: 1rem;
}
</style>